import type { FieldAttributes } from "formik";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";

import { FieldLabel, FieldTextareaDynamic, FormRowField } from "../forms";
import type { ClickShieldProps } from "../pointer";
import { ClickShieldProvider, useClickShield } from "../pointer";

type CanvasInputProps = FieldAttributes<any> &
  ClickShieldProps & {
    name: string;
    placeholder: string;
    size?: "small" | "large";
    label?: string;
    disabled?: boolean;
    clickShield?: boolean;
    onTabKey?: () => void;
    onEnterKey?: () => void;
    onEscapeKey?: () => void;
  };

export type ImperativeActions = {
  focus: () => void;
};

export const CanvasInput = forwardRef<ImperativeActions, CanvasInputProps>((props, reference) => {
  const { clickShield = true, onShieldClick, ...rest } = props;

  const properties = { ...rest, reference };

  return clickShield ? (
    <ClickShieldProvider onShieldClick={onShieldClick}>
      <CanvasInputCore {...properties} />
    </ClickShieldProvider>
  ) : (
    <CanvasInputCore {...properties} />
  );
});
CanvasInput.displayName = "CanvasInput";

const CanvasInputCore = ({
  size = "small",
  name,
  label,
  placeholder,
  onBlur,
  onFocus,
  onTabKey,
  onEnterKey,
  onEscapeKey,
  reference,
  ...rest
}: CanvasInputProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  const [expand, setExpand] = useState(false);

  const { activate, deactivate } = useClickShield();

  const setFocusToEndOfTextarea = useCallback(() => {
    const textarea = ref.current;

    if (textarea) {
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      textarea.focus();
    }
  }, []);

  const handleFocus = useCallback(() => {
    deactivate();
    setFocusToEndOfTextarea();
    onFocus?.();
    setExpand(true);
  }, [deactivate, onFocus, setFocusToEndOfTextarea]);

  const handleImperativeFocus = useCallback(() => {
    handleFocus();
    ref.current?.focus();
  }, [handleFocus]);

  useImperativeHandle(reference, () => ({ focus: handleImperativeFocus }));

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Tab") {
        onTabKey?.();
      }
    },
    [onTabKey],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    activate();
    setExpand(false);
  }, [activate, onBlur]);

  return (
    <FormRowField name={name}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <FieldTextareaDynamic
        {...rest}
        ref={ref}
        data-click-shield-target
        size={size}
        placeholder={placeholder}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onClick={(event) => event.stopPropagation()}
        onKeyDownCapture={handleKeyDown}
        onEnterKey={onEnterKey}
        onEscapeKey={onEscapeKey}
        cols={1}
        expand={expand}
      />
    </FormRowField>
  );
};
